import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { FlagType } from '@af/jira-flags-types';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { convertToProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import { graphql, useFragment } from 'react-relay';
import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';
import {
	EditionAwareness as EditionAwarenessImpl,
	type EditionAwarenessProps,
} from '@atlassian/edition-awareness/edition-awareness';
import type { Flag, UserRole } from '@atlassian/edition-awareness/types';
import { EditionAwarenessUpsell } from '@atlassian/edition-awareness/ui/edition-awareness-upsell';
import { useIsEditionAwarenessPillDismissed } from '@jira/growth__jira-edition-awareness-for-standard-to-premium/src/services/is-ea-pill-dismissed/index.tsx';
import { useIsEligibleForUpsellEa } from '@jira/growth__jira-edition-awareness-for-standard-to-premium/src/services/is-eligible-for-upsell-ea/index.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import { memoizedFetchSitePersonalization } from '@jira/platform__services-personalization/src/services/site/index.tsx';
import type { Trait } from '@jira/platform__services-personalization/src/types.tsx';
import {
	ContextualAnalyticsData,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { RenderOnClient } from '@jira/ssr__ssr-toolkit/src/render-on-client.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { useOrgId } from '@atlassian/jira-tenant-context-controller/src/components/org-id/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
// eslint-disable-next-line jira/restricted/fedramp-isolated-cloud
import { isFedRamp } from '@atlassian/atl-context';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { functionUnionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useCurrentUser as useCurrentUserBase } from '@jira/platform__services-user-current/src/main.tsx';
import { useQueryParam } from '@atlassian/react-resource-router';
import { useProductAndEdition } from '../services/use-product-and-edition.tsx';

import type { EditionAwareness_query$key } from './__generated__/EditionAwareness_query.graphql';

const useCurrentUserGated = functionUnionWithCondition(
	() => fg('asf-944-account-sync'),
	useCurrentUserBase,
	() => ({ data: { user: null } }),
);

const EditionAwarenessPlatformInner = (props: EditionAwarenessProps) => {
	const { isDismissed, handleDismiss } = useIsEditionAwarenessPillDismissed(props.product);
	const { isEligibleForUpsellEa } = useIsEligibleForUpsellEa({
		product: props.product,
		edition: props.edition,
		isSiteAdmin: Boolean(props.userRoles?.includes('SITE_ADMIN')),
	});

	return isEligibleForUpsellEa ? (
		<EditionAwarenessUpsell
			{...(({ product, cloudId, edition, userRoles, originalProjectTypeWasCore }) => ({
				product,
				cloudId,
				edition,
				userRoles,
				originalProjectTypeWasCore,
				hasDismissedButton: isDismissed,
				onDismissButton: handleDismiss,
			}))(props)}
		/>
	) : (
		<EditionAwarenessImpl {...props} />
	);
};

const EditionAwarenessPlatform = ({
	editionAwarenessQueryFragment,
}: {
	editionAwarenessQueryFragment: EditionAwareness_query$key;
}) => {
	const cloudId = useCloudId();
	const orgId = useOrgId();
	const accountId = useAccountId();
	const environment = useEnvironment();
	const isSiteAdmin = useIsSiteAdmin();
	const isJiraAdmin = useIsAdmin();
	const isEditionAwarenessDisabled = fg('aem_edition_awareness')
		? !isFeatureEnabled('jira', 'jira-edition-awareness')
		: isFedRamp();
	const { data: currentUserData } = useCurrentUserGated();

	const projectQueryData = useFragment(
		graphql`
			fragment EditionAwareness_query on Query {
				jira_projectByIdOrKey(cloudId: $cloudId, idOrKey: $projectIdOrKey)
					@include(if: $shouldFetchProjectTypeForSettingsAndEA) {
					projectType
				}
			}
		`,
		editionAwarenessQueryFragment,
	);

	const projectTypeFromQuery = projectQueryData?.jira_projectByIdOrKey?.projectType;

	const { product, edition, currentRoute, originalProjectTypeWasCore } = useProductAndEdition(
		projectTypeFromQuery ? convertToProjectType(projectTypeFromQuery) : undefined,
	);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Planned to be expanded as we add new actions
	const getAction = useCallback(async () => null, []);

	const userRoles: UserRole[] = useMemo(() => {
		const roles: UserRole[] = [];
		if (isSiteAdmin) {
			roles.push('SITE_ADMIN');
		}
		if (isJiraAdmin) {
			roles.push('JIRA_ADMIN');
		}
		return roles;
	}, [isSiteAdmin, isJiraAdmin]);

	const [siteTraits, setSiteTraits] = useState<Trait[] | undefined>(() => {
		// Calculate which editions need to fetch traits
		if (edition === 'free' || (edition === 'standard' && product === 'jira')) {
			return undefined;
		}

		return [];
	});

	useEffect(() => {
		if (siteTraits === undefined) {
			memoizedFetchSitePersonalization({ cloudId })
				.then((traits: Trait[]) => {
					setSiteTraits(traits);
				})
				.catch((e) => {
					setSiteTraits([]);
					const event = createAnalyticsEvent({});
					fireErrorAnalytics({
						event,
						meta: {
							id: 'editionAwarenessGrowthCohortTrait',
							packageName: 'edition-awareness',
							teamName: 'growth-tako',
						},
						error: e,
						sendToPrivacyUnsafeSplunk: true,
					});
				});
		}
	}, [cloudId, createAnalyticsEvent, edition, siteTraits]);

	// We need to wait for the siteType to be set before rendering the button
	const canRender = siteTraits !== undefined;

	const { showFlag, dismissFlag } = useFlagsService();

	const handleShowFlag = useCallback(
		(flag: Flag) => {
			const { appearance, id, ...flagArgs } = flag;

			const type: FlagType = !appearance || appearance === 'normal' ? 'info' : appearance;

			showFlag({
				...flagArgs,
				// Assumes that when we're showing flags from edition awareness they are transactional
				messageType: 'transactional',
				messageId: id,
				type,
			});
		},
		[showFlag],
	);

	const [isStarVariantConfig] = UNSAFE_noExposureExp('jsmgrowth466_star');
	const isStarVariant = isStarVariantConfig.get('isEnabled', false);
	const [showEditionBenefitsModal, setShowEditionBenefitsModal] = useQueryParam(
		'showEditionBenefitsModal',
	);
	const shouldAutoOpen = showEditionBenefitsModal === 'true';
	const resetAutoOpen = useCallback(() => {
		setShowEditionBenefitsModal(undefined);
	}, [setShowEditionBenefitsModal]);

	const editionAwarenessProps: EditionAwarenessProps | null = useMemo(() => {
		if (!product || !edition) {
			return null;
		}

		if (!accountId) {
			// We don't render when the customer is not logged in
			return null;
		}

		return {
			edition,
			cloudId,
			orgId,
			product,
			environment,
			userRoles,
			siteTraits,
			canRender,
			getAction,
			showFlag: handleShowFlag,
			hideFlag: dismissFlag,
			currentRoute,
			originalProjectTypeWasCore,
			// @ts-expect-error - TS2339 - Property 'emailAddress' does not exist on type 'DataBasic | (DataBasic & ResponseSuccess)'.
			userEmail: currentUserData?.user?.emailAddress ?? null,
			...(isStarVariant
				? {
						shouldAutoOpen,
						onAutoOpen: resetAutoOpen,
					}
				: {}),
		};
	}, [
		accountId,
		canRender,
		cloudId,
		orgId,
		edition,
		environment,
		userRoles,
		product,
		siteTraits,
		getAction,
		handleShowFlag,
		dismissFlag,
		currentRoute,
		originalProjectTypeWasCore,
		currentUserData,
		shouldAutoOpen,
		resetAutoOpen,
		isStarVariant,
	]);

	const stableAnalyticsAttributes = useMemo(
		() => ({ isSiteAdmin, product, edition }),
		[isSiteAdmin, product, edition],
	);

	if (!editionAwarenessProps) {
		return null;
	}

	if (isEditionAwarenessDisabled) {
		return null;
	}

	return (
		<ContextualAnalyticsData
			attributes={
				fg('navx-4728-jira-stability-fixes')
					? stableAnalyticsAttributes
					: {
							isSiteAdmin,
							product,
							edition,
						}
			}
		>
			{edition === 'premium' && product === 'jira' ? (
				<EditionAwarenessImpl {...editionAwarenessProps} />
			) : (
				<EditionAwarenessPlatformInner {...editionAwarenessProps} />
			)}
		</ContextualAnalyticsData>
	);
};

export const EditionAwareness = ({
	editionAwarenessQueryFragment,
}: {
	editionAwarenessQueryFragment: EditionAwareness_query$key;
}): React.JSX.Element | null => {
	return (
		<RenderOnClient>
			<UFOSegment name="edition-awareness">
				<JSErrorBoundary
					id="EditionAwareness"
					packageName="jiraNavigationAppsSidebarEditionAwareness"
					fallback="unmount"
					teamName="growth-tako"
				>
					<EditionAwarenessPlatform editionAwarenessQueryFragment={editionAwarenessQueryFragment} />
				</JSErrorBoundary>
			</UFOSegment>
		</RenderOnClient>
	);
};
